/**
 * QueriesModule — Phase 106 R13: Communications Channel (Queries Raised).
 *
 * Threaded internal + external messaging for the audit portal.
 * Left panel : list of threads (table-design1) with channel + status badges.
 * Right panel: thread detail — ordered messages + reply composer.
 * Toolbar    : "Raise Query" button (modal: subject, channel, linked
 *              procedure/program, first message), channel filter, status filter.
 *
 * Layout follows the shared split-card design system (see ReportTemplateNetBody):
 *   netbody-card side-nav-card  (left)  +  netbody-card side-content-card (right).
 * It is EMBEDDABLE — AuditNetBody / Layout5 supply the outer
 * module-layout wrapper, so this component must NOT add its own.
 *
 * Data: communicationsService from auditService.ts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Plus, Send, Paperclip, Save, Search, ChevronDown, ChevronUp, Download, Eye, Trash2 } from 'lucide-react';

import Button from '../../components/common/Button';
import Tip from '../../components/common/Tip';
import Modal from '../../components/common/Modal';
import FormControl from '../../components/common/FormControl';
import Pagination from '../../components/common/Pagination';
import Skeleton from '../../components/common/Skeleton';
import Toast, { ToastMessage } from '../../components/common/Toast';
import NoDataFound from '../../components/common/NoDataFound';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { FieldSpec } from '../../validation/engine';
import {
  communicationsService,
  fileService,
  CommThread,
  CommThreadDetail,
  CommMessage,
} from '../../services/auditService';
import apiClient from '../../services/apiService';
import { isPickableUser } from '../../utils/userPickable';

type Channel = 'internal' | 'external';

// Per-channel labels so the same module renders as either "Queries Raised"
// (internal, within the agency audit team) or "Communication" (external, to GRC).
const CHANNEL_PAGE_COPY: Record<Channel, { title: string; raiseLabel: string; emptyTitle: string }> = {
  internal: { title: 'Queries', raiseLabel: 'Create', emptyTitle: 'No queries yet' },
  external: { title: 'Communication', raiseLabel: 'Create', emptyTitle: 'No communications yet' },
};

// ---------------------------------------------------------------------------
// Channel / Status chips — app-wide status-badge variants (global.css)
// ---------------------------------------------------------------------------

const CHANNEL_VARIANT: Record<string, string> = { internal: 'info', external: 'warning' };
const STATUS_VARIANT: Record<string, string> = { open: 'success', closed: 'neutral' };

// Default thread page size; controllable per-component via the pager dropdown.
const PAGE_SIZE = 10;

function Chip({ label, variant }: { label: string; variant: string }) {
  return (
    <span className={`status-badge sm ${variant}`} style={{ textTransform: 'capitalize' }}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Raise Query Modal
// ---------------------------------------------------------------------------

interface RaiseQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (thread: CommThreadDetail) => void;
  onError: (message: string) => void;
  channelLock?: Channel;
}

const RaiseQueryModal: React.FC<RaiseQueryModalProps> = ({ isOpen, onClose, onCreated, onError, channelLock }) => {
  const { state } = useAuth();
  const vh = useFieldValidation();
  const entityId = state.user?.entity_id ?? 0;
  const userId = state.user?.id ?? 0;
  const [subject, setSubject] = useState('');
  const [channel, setChannel] = useState<Channel>(channelLock ?? 'internal');
  const [firstMessage, setFirstMessage] = useState('');
  const [procedureId, setProcedureId] = useState('');
  const [programId, setProgramId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [grcUsers, setGrcUsers] = useState<{ id: number; full_name: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [recipientIds, setRecipientIds] = useState<number[]>([]);

  // Keep channel pinned to the module's lock (internal=Queries, external=Communication).
  useEffect(() => { if (channelLock) setChannel(channelLock); }, [channelLock, isOpen]);

  // Load GRC users when modal opens in external mode.
  useEffect(() => {
    if (!isOpen || channel !== 'external') return;
    let cancelled = false;
    setLoadingUsers(true);
    apiClient.get('/api/v1/usr/users', { params: { limit: 500 } }).then((resp: any) => {
      if (cancelled) return;
      const raw = resp?.data?.data ?? resp?.data ?? [];
      setGrcUsers((Array.isArray(raw) ? raw : []).filter(isPickableUser).map((u: any) => ({
        id: u.id,
        full_name: (u.first_name || u.last_name)
          ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()
          : (u.user_name ?? u.login_id ?? `User #${u.id}`),
      })));
      setLoadingUsers(false);
    }).catch(() => { if (!cancelled) { setGrcUsers([]); setLoadingUsers(false); } });
    return () => { cancelled = true; };
  }, [isOpen, channel]);

  const reset = () => {
    setSubject('');
    setChannel(channelLock ?? 'internal');
    setFirstMessage('');
    setProcedureId('');
    setProgramId('');
    setFile(null);
    setAttachOpen(false);
    setRecipientIds([]);
    setGrcUsers([]);
    setLoadingUsers(false);
    vh.reset();
  };

  const handleClose = () => { reset(); onClose(); };

  const isInvalid = !subject.trim() || !firstMessage.trim() || (channel === 'external' && recipientIds.length === 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isInvalid) return;
    const specs: FieldSpec[] = [
      { name: 'subject', code: 110, value: subject, required: true },
      { name: 'first_message', code: 110, value: firstMessage, required: true },
      { name: 'procedure_id', code: 202, value: procedureId },
      { name: 'program_id', code: 202, value: programId },
    ];
    if (!vh.validateAll(specs)) return;
    setSaving(true);
    try {
      let attachmentFileId: number | undefined;
      if (file) {
        const up = await fileService.upload(file, entityId, userId);
        attachmentFileId = up.id;
      }
      const thread = await communicationsService.create({
        subject: subject.trim(),
        channel,
        first_message: firstMessage.trim(),
        procedure_instance_id: procedureId ? parseInt(procedureId, 10) : undefined,
        program_instance_id: programId ? parseInt(programId, 10) : undefined,
        attachment_file_id: attachmentFileId,
        recipient_user_ids: channel === 'external' ? recipientIds : undefined,
      });
      onCreated(thread);
      reset();
    } catch (err: any) {
      onError(err?.response?.data?.detail || err.message || 'Failed to raise query.');
    } finally {
      setSaving(false);
    }
  };

  const resourceLabel = (channelLock ?? channel) === 'external' ? 'Communication' : 'Query';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" data-testid="raise-query-modal">
      <Modal.Header>{`Create ${resourceLabel}`}</Modal.Header>
      <Modal.Body>
        <form
          id="raise-query-form"
          onSubmit={handleSubmit}
          noValidate
          className="grid grid-cols-2 gap-x-6 gap-y-4"
        >
          <ValidatedInput
            vh={vh}
            name="subject"
            code={110}
            type="text"
            label="Subject"
            placeholder="Briefly describe your query"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            maxLength={500}
            required
            className="col-span-2"
            data-testid="raise-query-subject"
          />

          {!channelLock && (
            <FormControl.SearchSelect
              label="Channel"
              value={channel}
              onChange={value => setChannel((value as Channel) ?? 'internal')}
              options={[
                { value: 'internal', label: 'Internal (within audit team)' },
                { value: 'external', label: 'External (audit ↔ GRC team)' },
              ]}
              className="col-span-2"
              data-testid="raise-query-channel"
            />
          )}

          {/* Procedure ID + Program ID — two in one row */}
          <ValidatedInput
            vh={vh}
            name="procedure_id"
            code={202}
            type="number"
            label="Link to Procedure Instance ID (optional)"
            placeholder="Leave blank if not applicable"
            value={procedureId}
            onChange={e => setProcedureId(e.target.value)}
            min={1}
            data-testid="raise-query-procedure-id"
          />

          <ValidatedInput
            vh={vh}
            name="program_id"
            code={202}
            type="number"
            label="Link to Program Instance ID (optional)"
            placeholder="Leave blank if not applicable"
            value={programId}
            onChange={e => setProgramId(e.target.value)}
            min={1}
            data-testid="raise-query-program-id"
          />

          <ValidatedTextarea
            vh={vh}
            name="first_message"
            code={110}
            label="Opening Message"
            placeholder="Describe your query in detail…"
            value={firstMessage}
            onChange={e => setFirstMessage(e.target.value)}
            rows={4}
            required
            className="col-span-2"
            data-testid="raise-query-first-message"
          />

          {channel === 'external' && (
            <div className="col-span-2">
              {loadingUsers ? (
                <FormControl.MultiSelect
                  label="Recipients"
                  required
                  placeholder="Loading users…"
                  options={[]}
                  value={recipientIds}
                  onChange={() => {}}
                  disabled
                />
              ) : grcUsers.length === 0 ? (
                <>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
                    Recipients <span style={{ color: 'var(--danger, #dc2626)' }}>*</span>
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--danger, #dc2626)' }}>
                    Failed to load users. Please close and reopen.
                  </span>
                </>
              ) : (
                <FormControl.MultiSelect
                  label="Recipients"
                  required
                  placeholder="Select recipients…"
                  options={grcUsers.map(u => ({ value: u.id, label: u.full_name }))}
                  value={recipientIds}
                  onChange={(vals) => setRecipientIds(vals.map(Number))}
                  error={recipientIds.length === 0 ? 'At least one recipient is required for external communications.' : undefined}
                />
              )}
            </div>
          )}

          <div className="col-span-2">
            <button
              type="button"
              className="observation-evidence-toggle"
              onClick={() => setAttachOpen(v => !v)}
              disabled={saving}
            >
              <Paperclip size={13} />
              <span>Attach File</span>
              {file && <span className="observation-evidence-badge">{file.name}</span>}
              {attachOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {attachOpen && (
              <div className="observation-evidence-panel">
                <FormControl.FileUpload
                  disabled={saving}
                  onChange={(files: FileList | null) => setFile(files && files[0] ? files[0] : null)}
                />
              </div>
            )}
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          form="raise-query-form"
          variant="fill"
          icon={Save}
          disabled={isInvalid || saving}
          data-testid="raise-query-submit"
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Avatar helpers + MessageAvatar / ChatMessage — Slack/WhatsApp-style bubbles
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  '#2e7d32', // brand-green
  '#1565c0', // blue
  '#6a1b9a', // purple
  '#c62828', // red
  '#e65100', // orange
  '#00695c', // teal
];

function avatarColor(name: string): string {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function nameInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function MessageAvatar({ name }: { name: string }) {
  return (
    <div className="chat-avatar" style={{ background: avatarColor(name) }} aria-label={name}>
      {nameInitials(name)}
    </div>
  );
}

// One chat row: avatar (others, first-in-group only) + bubble with sender name,
// body, optional attachment pill, and an in-bubble timestamp. Mine floats right
// (green tint, no avatar); others float left (neutral bubble + avatar).
function ChatMessage({
  msg,
  isGrouped,
  isMine,
  onAttachmentDownload,
  onAttachmentView,
  onAttachmentDelete,
}: {
  msg: CommMessage;
  isGrouped: boolean;
  isMine: boolean;
  onAttachmentDownload: (m: CommMessage) => void;
  onAttachmentView: (m: CommMessage) => void;
  onAttachmentDelete: (m: CommMessage) => void;
}) {
  const { formatDateTime } = useEntitySettings();
  const senderName = msg.sender_name ?? `User ${msg.sender_user_id}`;
  const timeStr = msg.created_on ? formatDateTime(msg.created_on) : '';

  const rowClass = [
    'chat-row',
    isMine ? 'chat-row--mine' : 'chat-row--theirs',
    !isGrouped ? 'chat-row--first' : '',
  ].filter(Boolean).join(' ');

  const bubbleClass = `chat-bubble ${isMine ? 'chat-bubble--mine' : 'chat-bubble--theirs'}`;

  return (
    <div className={rowClass} data-testid={`message-${msg.id}`}>
      {/* Avatar / placeholder — none for mine (right side); placeholder keeps
          grouped messages indented in line with the first one. */}
      {!isMine && (
        isGrouped ? <div className="chat-avatar--placeholder" /> : <MessageAvatar name={senderName} />
      )}

      <div className="chat-bubble-wrap">
        {!isMine && !isGrouped && <span className="chat-sender-name">{senderName}</span>}

        <div className={bubbleClass}>
          <p style={{ margin: 0 }}>{msg.body}</p>
          {msg.attachment_file_id && (
            <div className="chat-attachment" data-testid={`message-attachment-${msg.id}`}>
              <Paperclip size={12} />
              <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {msg.attachment_file_name ?? `Attachment #${msg.attachment_file_id}`}
              </span>
              <button
                type="button" className="devops-btn-icon" title="Download"
                onClick={() => onAttachmentDownload(msg)}
                data-testid={`message-attachment-download-${msg.id}`}
              >
                <Download size={13} />
              </button>
              <button
                type="button" className="devops-btn-icon" title="View"
                onClick={() => onAttachmentView(msg)}
                data-testid={`message-attachment-view-${msg.id}`}
              >
                <Eye size={13} />
              </button>
              {isMine && (
                <button
                  type="button" className="devops-btn-icon" title="Remove attachment"
                  style={{ color: 'var(--danger, #dc2626)' }}
                  onClick={() => onAttachmentDelete(msg)}
                  data-testid={`message-attachment-delete-${msg.id}`}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          )}
          <span className="chat-bubble-time">{timeStr}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread Detail Panel — renders INTO the right side-content-card
// ---------------------------------------------------------------------------

interface ThreadDetailPanelProps {
  thread: CommThreadDetail;
  onStatusChange: (updated: CommThread) => void;
  onMessagePosted: (msg: CommMessage) => void;
  onMessageUpdated?: (msg: CommMessage) => void;
  /** Hide the channel chip when the module is locked to one channel (it adds no info). */
  hideChannelChip?: boolean;
  /** 'card' (default) renders into the right side-content-card of the two-card
   *  split. 'modal' renders Modal.Header/Body/Footer — used by the Action-Centre
   *  responder, which opens a single thread in a dialog (no split). */
  presentation?: 'card' | 'modal';
}

const ThreadDetailPanel: React.FC<ThreadDetailPanelProps> = ({
  thread,
  onStatusChange,
  onMessagePosted,
  onMessageUpdated,
  hideChannelChip = false,
  presentation = 'card',
}) => {
  const { state } = useAuth();
  const { formatDateTime } = useEntitySettings();
  const entityId = state.user?.entity_id ?? 0;
  const userId = state.user?.id ?? 0;
  const [replyBody, setReplyBody] = useState('');
  const [replyFile, setReplyFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const replyTextareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread.messages]);

  const isClosed = (thread.status || '').toLowerCase() === 'closed';

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isClosed) return;            // closed threads are read-only
    if (!replyBody.trim() && !replyFile) return;
    setSending(true);
    setReplyError(null);
    try {
      let attachmentFileId: number | undefined;
      if (replyFile) {
        const up = await fileService.upload(replyFile, entityId, userId);
        attachmentFileId = up.id;
      }
      const msg = await communicationsService.postMessage(
        thread.id,
        replyBody.trim() || '(see attachment)',
        attachmentFileId,
      );
      onMessagePosted(msg);
      setReplyBody('');
      setReplyFile(null);
      // Auto-grow only fires on change; collapse the box back to one row after a
      // programmatic clear so the composer doesn't stay stretched.
      if (replyTextareaRef.current) replyTextareaRef.current.style.height = 'auto';
    } catch (err: any) {
      setReplyError(err?.response?.data?.detail || err.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleToggleStatus = async () => {
    setToggling(true);
    try {
      const newStatus = thread.status === 'open' ? 'closed' : 'open';
      const updated = await communicationsService.setStatus(thread.id, newStatus);
      onStatusChange(updated);
    } finally {
      setToggling(false);
    }
  };

  // ── Reusable pieces (shared by the card split and the responder modal) ──────
  // Only the originator can close/reopen the thread. Responders (recipients) just
  // reply — the status toggle is hidden for them.
  const canManageStatus = userId === thread.raised_by;
  const headerActions = (
    <>
      {!hideChannelChip && (
        <Chip label={thread.channel} variant={CHANNEL_VARIANT[thread.channel] ?? 'info'} />
      )}
      <Chip label={thread.status} variant={STATUS_VARIANT[thread.status] ?? 'success'} />
      {canManageStatus && (
        <Button
          variant={thread.status === 'open' ? 'secondary-outline' : 'outline'}
          onClick={handleToggleStatus}
          disabled={toggling}
          data-testid="thread-toggle-status"
        >
          {toggling ? '…' : thread.status === 'open' ? 'Close Thread' : 'Reopen'}
        </Button>
      )}
    </>
  );

  // Dot separator that allows the meta line to wrap between segments (plain
  // spaces, not &nbsp;, so a long Procedure/Program name can't force x-scroll).
  const metaDot = <span style={{ padding: '0 6px', opacity: 0.6 }}>·</span>;
  const metaLine = (
    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
      Raised by <strong>{thread.raised_by_name ?? `User ${thread.raised_by}`}</strong>
      {thread.created_on && (
        <>{metaDot}{formatDateTime(thread.created_on)}</>
      )}
      {thread.procedure_instance_id && (
        <>{metaDot}Procedure: {thread.procedure_instance_name ?? `#${thread.procedure_instance_id}`}</>
      )}
      {thread.program_instance_id && (
        <>{metaDot}Program: {thread.program_instance_description || thread.program_instance_code || `#${thread.program_instance_id}`}</>
      )}
    </div>
  );

  // Per-message attachment actions (issue #11) — passed down to ChatMessage.
  const handleAttachmentDownload = async (m: CommMessage) => {
    if (!m.attachment_file_id) return;
    try {
      await fileService.download(m.attachment_file_id, m.attachment_file_name ?? 'attachment');
    } catch (err: any) {
      setReplyError(err?.message || 'Could not download attachment.');
    }
  };
  const handleAttachmentView = async (m: CommMessage) => {
    if (!m.attachment_file_id) return;
    try {
      await fileService.open(m.attachment_file_id);
    } catch (err: any) {
      setReplyError(err?.message || 'Could not open attachment.');
    }
  };
  const handleAttachmentDelete = async (m: CommMessage) => {
    if (!m.attachment_file_id) return;
    if (!window.confirm('Remove this attachment from the message?')) return;
    try {
      const updated = await communicationsService.deleteMessageAttachment(thread.id, m.id);
      onMessageUpdated?.(updated);
    } catch (err: any) {
      setReplyError(err?.response?.data?.detail || err?.message || 'Could not remove attachment.');
    }
  };

  // Chat-style transcript (WhatsApp/Slack): mine align right (green-tint bubble,
  // no avatar), others align left (neutral bubble + colored avatar). Consecutive
  // messages from the same sender group together (avatar/name shown once).
  const messageItems = thread.messages.length === 0 ? (
    <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No messages yet.</p>
  ) : (
    thread.messages.map((msg, idx) => {
      const prevSender = idx > 0 ? thread.messages[idx - 1].sender_user_id : null;
      const isGrouped = prevSender === msg.sender_user_id;
      const isMine = msg.sender_user_id === userId;
      return (
        <ChatMessage
          key={msg.id}
          msg={msg}
          isGrouped={isGrouped}
          isMine={isMine}
          onAttachmentDownload={handleAttachmentDownload}
          onAttachmentView={handleAttachmentView}
          onAttachmentDelete={handleAttachmentDelete}
        />
      );
    })
  );

  // Reply composer — auto-growing textarea · attach · send on one row. The
  // .chat-composer top border is dropped in modal mode (Modal.Footer already
  // has its own divider). Closed threads are read-only: the composer is
  // replaced by a notice so a user cannot type or send into a closed thread.
  const replyForm = isClosed ? (
    <div
      className="chat-composer chat-composer--closed"
      style={{
        ...(presentation === 'modal' ? { borderTop: 'none', padding: 0, width: '100%' } : {}),
        display: 'flex',
        justifyContent: 'center',
      }}
      data-testid="reply-closed-notice"
    >
      <span
        style={{
          fontSize: 13,
          color: 'var(--gray-500, #6b7280)',
          fontStyle: 'italic',
          padding: '8px 12px',
          textAlign: 'center',
        }}
      >
        This thread is closed. Reopen it to continue the conversation.
      </span>
    </div>
  ) : (
    <form
      onSubmit={handleReply}
      className="chat-composer"
      style={presentation === 'modal' ? { borderTop: 'none', padding: 0, width: '100%' } : undefined}
      data-testid="reply-form"
    >
      {replyError && <span className="chat-error">{replyError}</span>}
      <div className="chat-composer-row">
        {/* Capsule: attach icon + growing text input together (WhatsApp style). */}
        <div className="chat-composer-input">
          <Tip text={replyFile ? replyFile.name : 'Attach file'} placement="top">
            <label
              className={`chat-composer-attach${replyFile ? ' chat-composer-attach--active' : ''}`}
              data-testid="reply-attach"
            >
              <Paperclip size={18} />
              <input
                type="file"
                className="chat-composer-file-input"
                disabled={sending}
                onChange={e => setReplyFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
              />
            </label>
          </Tip>
          <textarea
            ref={replyTextareaRef}
            className="chat-composer-textarea"
            placeholder="Write a reply…"
            value={replyBody}
            onChange={e => {
              setReplyBody(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            rows={1}
            data-testid="reply-body"
            disabled={sending}
          />
        </div>
        <Button
          type="submit"
          variant="fill"
          icon={Send}
          disabled={sending || (!replyBody.trim() && !replyFile)}
          data-testid="reply-submit"
          aria-label={sending ? 'Sending' : 'Send'}
        />
      </div>
    </form>
  );

  // ── Modal presentation: subject in the dialog header, messages in the body,
  //    reply composer pinned in the footer. One thread, opened and answered in
  //    place — no left-list / right-detail split. ──────────────────────────────
  if (presentation === 'modal') {
    return (
      <>
        <Modal.Header actions={headerActions}>
          <MessageSquare size={16} strokeWidth={1.75} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={thread.subject}>
            {thread.subject}
          </span>
        </Modal.Header>
        <Modal.Body>
          {metaLine}
          <div className="chat-feed" style={{ marginTop: 12, padding: 0, flex: 'none' }} data-testid="thread-messages">
            {messageItems}
            <div ref={bottomRef} />
          </div>
        </Modal.Body>
        <Modal.Footer>{replyForm}</Modal.Footer>
      </>
    );
  }

  // ── Card presentation (two-card split, audit portal): heading bar + meta +
  //    scrolling messages + reply composer, all inside the right content card. ──
  return (
    <>
      <div className="side-content-card-heading" style={{ justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <MessageSquare size={16} strokeWidth={1.75} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={thread.subject}>
            {thread.subject}
          </span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {headerActions}
        </span>
      </div>

      <div style={{ padding: '8px 16px 0', width: '100%', minWidth: 0, flexShrink: 0, overflow: 'hidden', boxSizing: 'border-box' }}>{metaLine}</div>

      <div className="chat-feed netbody-content" data-testid="thread-messages">
        {messageItems}
        <div ref={bottomRef} />
      </div>

      {replyForm}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main QueriesModule component
// ---------------------------------------------------------------------------

interface QueriesModuleProps {
  /** Locks the module to one channel: 'internal' = Queries Raised,
   *  'external' = Communication. When set, the channel selector/filter are hidden
   *  and all threads + new threads use this channel. */
  channelLock?: Channel;
  /** When true, only threads where the current user is a recipient are listed.
   *  Used by the Action-Centre audit tab to show recipient communications. */
  recipientScope?: boolean;
  /** Reports the loaded thread total to the embedder (Action-Centre audit tab),
   *  so it can suppress its "No Audit Items Found" empty state when comms exist. */
  onCountChange?: (total: number) => void;
}

const QueriesModule: React.FC<QueriesModuleProps> = ({ channelLock, recipientScope, onCountChange }) => {
  const { formatDateTime } = useEntitySettings();
  const pageCopy = channelLock ? CHANNEL_PAGE_COPY[channelLock] : CHANNEL_PAGE_COPY.internal;
  const [threads, setThreads] = useState<CommThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Pagination — PAGE_SIZE is the default; the size is now controllable via state.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filters
  const [channelFilter, setChannelFilter] = useState<'' | 'internal' | 'external'>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'open' | 'closed'>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Raise query modal
  const [raiseOpen, setRaiseOpen] = useState(false);

  // Selected thread for detail view
  const [selectedThread, setSelectedThread] = useState<CommThreadDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetch
  // ---------------------------------------------------------------------------
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const result = await communicationsService.list({
        page,
        limit: pageSize,
        channel: channelLock ?? (channelFilter || undefined),
        status: statusFilter || undefined,
        recipient_only: recipientScope ? true : undefined,
        search: debouncedSearch || undefined,
      });
      setThreads(result.items);
      setTotalRecords(result.total);
      onCountChange?.(result.total);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load threads.' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, channelLock, channelFilter, statusFilter, debouncedSearch, recipientScope, onCountChange]);

  useEffect(() => { refresh(); }, [refresh]);

  // When the module's scope changes (e.g. navigating /queries ↔ /communication
  // reuses this same component instance with a different channelLock), the left
  // list refreshes but the right panel would otherwise keep the previously
  // selected thread from the other channel. Clear the selection + reset paging
  // so the detail panel falls back to the empty state until the user picks a
  // thread belonging to the new scope.
  useEffect(() => {
    setSelectedThread(null);
    setPage(1);
    setSearch('');
    setChannelFilter('');
    setStatusFilter('');
  }, [channelLock, recipientScope]);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [channelFilter, statusFilter, debouncedSearch, pageSize]);

  // ---------------------------------------------------------------------------
  // Thread selection
  // ---------------------------------------------------------------------------
  const handleRowClick = async (t: CommThread) => {
    setDetailLoading(true);
    try {
      const detail = await communicationsService.get(t.id);
      setSelectedThread(detail);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load thread.' });
    } finally {
      setDetailLoading(false);
    }
  };

  // Called when user posts a reply (optimistic update)
  const handleMessagePosted = (msg: CommMessage) => {
    setSelectedThread(prev =>
      prev ? { ...prev, messages: [...prev.messages, msg] } : prev
    );
    // Refresh list to update message_count + last_message_at
    refresh();
    setToast({ type: 'success', message: 'Reply sent.' });
  };

  // Called when a message changes in place (e.g. attachment removed — issue #11)
  const handleMessageUpdated = (msg: CommMessage) => {
    setSelectedThread(prev =>
      prev ? { ...prev, messages: prev.messages.map(m => m.id === msg.id ? { ...m, ...msg } : m) } : prev
    );
  };

  // Called when thread status is toggled
  const handleStatusChange = (updated: CommThread) => {
    setSelectedThread(prev => prev ? { ...prev, ...updated } : prev);
    setThreads(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    setToast({
      type: 'success',
      message: `Thread ${updated.status === 'closed' ? 'closed' : 'reopened'}.`,
    });
  };

  // Called when new thread created
  const handleThreadCreated = (thread: CommThreadDetail) => {
    setRaiseOpen(false);
    setSelectedThread(thread);
    refresh();
    setToast({ type: 'success', message: 'Query raised successfully.' });
  };

  // ---------------------------------------------------------------------------
  // Render — responder mode (Action-Centre embed)
  // ---------------------------------------------------------------------------
  // The Action-Centre audit tab embeds this below the findings list. A full-height
  // two-card split would overlap that content and forces the responder to hunt
  // through a left list. Instead: one flowing column of thread cards; clicking a
  // card opens that single thread in a dialog where the responder reads and replies.
  if (recipientScope) {
    const filtersActive = !!(debouncedSearch || statusFilter);
    // Only render the section if there are threads to show, or filters are active.
    // When empty with no filters, don't render — let the parent show its unified
    // "No Audit Items Found" empty state instead of duplicate messages.
    if (!loading && threads.length === 0 && !filtersActive) {
      return null;
    }

    return (
      <div data-testid="queries-module" className="comm-responder">
        {/* Toolbar: title on left · search · status filter · Create on right */}
        <div className="netbody-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h4 className="section-label" style={{ margin: 0 }}>{pageCopy.title}</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <FormControl.Input
              type="text"
              placeholder={`Search ${pageCopy.title.toLowerCase()}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="fg--no-margin"
              data-testid="query-search"
              style={{ width: 200 }}
            />
            <FormControl.SearchSelect
              value={statusFilter}
              onChange={v => setStatusFilter((v ?? '') as '' | 'open' | 'closed')}
              className="filter-status fg--no-margin"
              data-testid="query-filter-status"
              noSearchIcon
              options={[
                { value: '', label: 'All' },
                { value: 'open', label: 'Open' },
                { value: 'closed', label: 'Closed' },
              ]}
            />
            <Button variant="fill" icon={Plus} onClick={() => setRaiseOpen(true)} data-testid="raise-query-btn">
              {pageCopy.raiseLabel}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="action-card-grid action-card-grid--single">
            {[0, 1, 2].map(i => (
              <div key={i} className="action-card" style={{ pointerEvents: 'none' }} aria-hidden="true">
                <div className="action-card__header">
                  <div className="action-card__icon" style={{ background: 'var(--skeleton-bg)', minWidth: 34, minHeight: 34 }} />
                  <div className="action-card__title-block">
                    <Skeleton.Box width="65%" height={14} />
                    <Skeleton.Box width="45%" height={12} style={{ marginTop: 8 }} />
                  </div>
                  <div style={{ flexShrink: 0, width: 70, display: 'flex', gap: 6 }}>
                    <Skeleton.Box width={30} height={20} radius={12} />
                    <Skeleton.Box width={30} height={20} radius={12} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : threads.length === 0 ? (
          <NoDataFound
            title={filtersActive ? 'No matches' : pageCopy.emptyTitle}
            message={filtersActive
              ? 'No communications match your search or filter.'
              : 'No communications addressed to you yet.'}
          />
        ) : (
          <div className="action-card-grid action-card-grid--single">
            {threads.map(t => {
              const count = t.message_count ?? 0;
              return (
                <button
                  key={t.id}
                  type="button"
                  className="action-card"
                  onClick={() => handleRowClick(t)}
                  data-testid={`thread-row-${t.id}`}
                  title={t.subject}
                >
                  <div className="action-card__header">
                    <div className="action-card__icon">
                      <MessageSquare size={16} />
                    </div>
                    <div className="action-card__title-block">
                      <span className="action-card__title">{t.subject}</span>
                      <span className="action-card__subtitle">
                        Raised by {t.raised_by_name ?? `User ${t.raised_by}`}
                        {t.last_message_at ? ` · ${formatDateTime(t.last_message_at)}` : ''}
                      </span>
                    </div>
                    <div className="action-card__badge" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Chip label={t.status} variant={STATUS_VARIANT[t.status] ?? 'success'} />
                      <span
                        style={{
                          flexShrink: 0, fontSize: 11, fontWeight: 600, minWidth: 20,
                          textAlign: 'center', padding: '1px 6px', borderRadius: 9999,
                          background: 'var(--border-subtle)', color: 'var(--text-tertiary)',
                        }}
                        title={`${count} message${count === 1 ? '' : 's'}`}
                      >
                        {count}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <Pagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          recordLabel="threads"
        />

        {/* Open one thread → read + reply in a dialog. */}
        <Modal
          isOpen={detailLoading || !!selectedThread}
          onClose={() => setSelectedThread(null)}
          size="lg"
          closeOnEscape
          closeOnOverlayClick
          data-testid="comm-thread-modal"
        >
          {detailLoading && !selectedThread ? (
            <>
              <Modal.Header>Loading…</Modal.Header>
              <Modal.Body>
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                  Loading thread…
                </div>
              </Modal.Body>
            </>
          ) : selectedThread ? (
            <ThreadDetailPanel
              thread={selectedThread}
              onStatusChange={handleStatusChange}
              onMessagePosted={handleMessagePosted}
              hideChannelChip={!!channelLock}
              presentation="modal"
            />
          ) : null}
        </Modal>

        <RaiseQueryModal
          isOpen={raiseOpen}
          onClose={() => setRaiseOpen(false)}
          onCreated={handleThreadCreated}
          onError={message => setToast({ type: 'error', message })}
          channelLock={channelLock}
        />

        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — full module (audit portal): two-card split
  // ---------------------------------------------------------------------------
  return (
    <div className="flex h-full overflow-hidden" data-testid="queries-module">
      {/* ── Left nav card: thread picker (fixed-zone: heading + filters
              pinned top, list scrolls, pager pinned bottom) ──────────────── */}
      <div className="netbody-card side-nav-card side-nav-card--fixed">
        <div
          className="side-nav-card-heading"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span>{pageCopy.title}</span>
          <Button
            variant="fill"
            icon={Plus}
            onClick={() => setRaiseOpen(true)}
            data-testid="raise-query-btn"
          />
        </div>

        {/* Search + filters on a single row — fixed zone under the heading.
            Search (left, grows) replaces the page-size selector; the remaining
            filters sit on the right of the same row. */}
        <div className="side-nav-card-search" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Search grows to fill the row; the flex/width sit on the .fg
              wrapper via CSS (.side-nav-card-search > .fg), not the <input>. */}
          <FormControl.Input
            type="text"
            placeholder={`Search ${pageCopy.title.toLowerCase()}…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="fg--no-margin"
            data-testid="query-search"
          />
          {!channelLock && (
            <FormControl.SearchSelect
              value={channelFilter}
              onChange={v => setChannelFilter((v ?? '') as '' | 'internal' | 'external')}
              className="filter-status fg--no-margin side-nav-filter--channel"
              data-testid="query-filter-channel"
              noSearchIcon
              options={[
                { value: '', label: 'All channels' },
                { value: 'internal', label: 'Internal' },
                { value: 'external', label: 'External' },
              ]}
            />
          )}

          <FormControl.SearchSelect
            value={statusFilter}
            onChange={v => setStatusFilter((v ?? '') as '' | 'open' | 'closed')}
            className="filter-status fg--no-margin"
            data-testid="query-filter-status"
            noSearchIcon
            options={[
              { value: '', label: 'All' },
              { value: 'open', label: 'Open' },
              { value: 'closed', label: 'Closed' },
            ]}
          />
        </div>

        {/* Thread list — side-panel menu items (the ONLY scrolling region) */}
        <nav aria-label={pageCopy.title}>
          {loading &&
            // icon · subject (width varies per row) · count chip — mirrors the real row.
            [0, 1, 2, 3, 4].map(i => {
              const nameWidths = ['72%', '55%', '84%', '48%', '66%'];
              return (
                <div
                  key={i}
                  className="side-panel-menu-item"
                  style={{ pointerEvents: 'none' }}
                  aria-hidden="true"
                >
                  <Skeleton.Box width={nameWidths[i]} height={11} style={{ flex: '0 1 auto' }} />
                  <Skeleton.Circle size={18} style={{ marginLeft: 'auto' }} />
                </div>
              );
            })}

          {!loading && threads.length === 0 && (
            <NoDataFound
              title={debouncedSearch || channelFilter || statusFilter ? 'No matches' : pageCopy.emptyTitle}
              message={debouncedSearch || channelFilter || statusFilter
                ? 'No threads match your search or filters.'
                : channelLock === 'external'
                  ? 'Start a communication with the GRC team — attach data or request a reply.'
                  : 'Raise a query within the agency audit team.'}
            />
          )}

          {!loading && threads.map(t => {
            const active = selectedThread?.id === t.id;
            const count = t.message_count ?? 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleRowClick(t)}
                className={`side-panel-menu-item${active ? ' side-panel-menu-item--active' : ''}`}
                data-testid={`thread-row-${t.id}`}
                title={t.subject}
              >
                <span className="side-panel-menu-item__label">
                  {t.subject}
                </span>
                <span
                  className="side-panel-menu-item__count"
                  title={`${count} message${count === 1 ? '' : 's'}`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Pagination footer — fixed bottom zone */}
        <div className="side-nav-card-footer">
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            recordLabel="threads"
          />
        </div>
      </div>

      {/* ── Right content card: thread detail (fixed heading, body scrolls) ─ */}
      <div className="netbody-card side-content-card side-content-card--fixed flex flex-col flex-1 min-w-0">
        {detailLoading && (
          <>
            {/* Heading skeleton — icon · title · status · action */}
            <div className="side-content-card-heading" style={{ justifyContent: 'space-between', gap: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <Skeleton.Circle size={16} />
                <Skeleton.Box width={160} height={13} />
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <Skeleton.Pill width={56} height={20} />
                <Skeleton.Box width={92} height={30} radius={8} />
              </span>
            </div>

            {/* Meta line skeleton */}
            <div style={{ padding: '10px 16px 0' }}>
              <Skeleton.Box width={280} height={11} />
            </div>

            {/* Chat bubble skeletons — alternating left / right */}
            <div className="chat-feed">
              {[
                { mine: false, w: 150 },
                { mine: false, w: 210 },
                { mine: true, w: 120 },
                { mine: false, w: 180 },
                { mine: true, w: 160 },
              ].map((b, i) => (
                <div
                  key={i}
                  className={`chat-row ${b.mine ? 'chat-row--mine' : 'chat-row--theirs'} chat-row--first`}
                  aria-hidden="true"
                >
                  {!b.mine && <Skeleton.Circle size={34} />}
                  <Skeleton.Box width={b.w} height={38} radius={14} />
                </div>
              ))}
            </div>
          </>
        )}
        {!detailLoading && selectedThread && (
          <ThreadDetailPanel
            thread={selectedThread}
            onStatusChange={handleStatusChange}
            onMessagePosted={handleMessagePosted}
            onMessageUpdated={handleMessageUpdated}
            hideChannelChip={!!channelLock}
          />
        )}
        {!detailLoading && !selectedThread && (
          <>
            <div className="side-content-card-heading">Messages</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32, textAlign: 'center' }}>
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 84, height: 84, borderRadius: 9999,
                  background: 'var(--brand-subtle)', color: 'var(--interactive-primary-bg)',
                }}
              >
                <MessageSquare size={38} strokeWidth={1.5} />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                  Select a thread to view messages
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
                  Pick a query from the list on the left.
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Raise Query modal */}
      <RaiseQueryModal
        isOpen={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onCreated={handleThreadCreated}
        onError={message => setToast({ type: 'error', message })}
        channelLock={channelLock}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default QueriesModule;
